/* Shower Simply commerce behavior. Loaded with defer after ss-brand.js.
   - Delivery block: ZIP prefill and save (localStorage 'ss-fit', shared with the fit bar), checkout note,
     and the carrier-rate hook (getFreightRate).
   - Plan drawing: swaps in the drawing for the selected variant.
   - Spec downloads: adds file sizes.
   - Fit results: reveals the section for ?w=&d=, tags and sorts cards that fit, lets shoppers change size.
   - Trade form: license number required for contractors.
   Exposed as window.ShowerSimply for later integrations. */
(() => {
  const KEY = 'ss-fit';
  const readFit = () => {
    try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch (e) { return {}; }
  };
  const writeFit = (patch) => {
    try { localStorage.setItem(KEY, JSON.stringify({ ...readFit(), ...patch })); } catch (e) { /* storage unavailable */ }
  };
  const ZIP_RE = /^\d{5}$/;

  /* ---------------------------------------------------------------------------------------------
     CARRIER-RATE HOOK
     The Delivery block element carries data-ss-rate-endpoint (empty today), data-product-id,
     data-variant-id and data-ship-weight-lb. When a carrier-rate API is chosen:
       1. put its URL in data-ss-rate-endpoint in blocks/ss-delivery.liquid (or a block setting);
       2. implement the request below and return { amount, currency } or null;
       3. render the result in showDeliveryResult(), clearly labelled as an estimate.
     Until then this returns null and shoppers see only the checkout note and the phone link.
     Never show a number that did not come from the carrier.
     --------------------------------------------------------------------------------------------- */
  async function getFreightRate({ endpoint, zip, productId, variantId, quantity, weightLb }) {
    if (!endpoint) return null;
    // Future: return fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ zip, productId, variantId, quantity, weightLb, liftgate: true }) })
    //   .then((r) => (r.ok ? r.json() : null)).catch(() => null);
    void zip; void productId; void variantId; void quantity; void weightLb;
    return null;
  }

  /* ---------- Delivery block ---------- */
  function currentVariantId(scope) {
    const section = scope.closest('.shopify-section') || document;
    const input = section.querySelector('form[action*="/cart/add"] [name="id"]');
    if (input && input.value) return input.value;
    const fromUrl = new URLSearchParams(location.search).get('variant');
    return fromUrl || scope.dataset.variantId || '';
  }

  function currentQuantity(scope) {
    const section = scope.closest('.shopify-section') || document;
    const input = section.querySelector('form[action*="/cart/add"] [name="quantity"], input[name="quantity"]');
    const n = input ? Number(input.value) : 1;
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  function showDeliveryResult(root, zip, rate) {
    const region = root.querySelector('[data-ss-delivery-result]');
    const template = root.querySelector('template[data-ss-delivery-template]');
    if (!region || !template) return;
    const content = template.content.cloneNode(true);
    if (rate && rate.quote) {
      const p = document.createElement('p');
      p.className = 'ss-delivery__estimate';
      p.textContent = 'Freight to ' + rate.state + ' is quoted by phone. Call us and we will price it the same day.';
      content.prepend(p);
    } else if (rate && rate.amount) {
      const p = document.createElement('p');
      p.className = 'ss-delivery__estimate';
      const strong = document.createElement('strong');
      strong.textContent = '$' + Number(rate.amount).toLocaleString('en-US');
      p.append('Estimated freight to ', zip, ': ', strong, ' for home delivery with liftgate.');
      content.prepend(p);
    }
    const msg = content.querySelector('.ss-delivery__msg');
    if (msg) {
      const [before, ...rest] = msg.textContent.split('[zip]');
      msg.textContent = before;
      if (rest.length) {
        const strong = document.createElement('strong');
        strong.textContent = zip;
        msg.append(strong, rest.join('[zip]'));
      }
    }
    region.replaceChildren(content);
  }

  function initDelivery(scope = document) {
    const saved = readFit();
    scope.querySelectorAll('[data-ss-delivery]').forEach((root) => {
      if (root.dataset.ssReady) return;
      root.dataset.ssReady = '1';
      const form = root.querySelector('[data-ss-delivery-form]');
      const input = root.querySelector('[data-ss-delivery-zip]');
      const error = root.querySelector('[data-ss-delivery-error]');
      if (!form || !input) return;
      if (saved.zip && ZIP_RE.test(String(saved.zip))) input.value = saved.zip;

      const setError = (on) => {
        if (!error) return;
        error.hidden = !on;
        if (on) {
          input.setAttribute('aria-invalid', 'true');
          input.setAttribute('aria-describedby', error.id);
        } else {
          input.removeAttribute('aria-invalid');
          input.removeAttribute('aria-describedby');
        }
      };

      input.addEventListener('input', () => {
        const digits = input.value.replace(/\D/g, '').slice(0, 5);
        if (digits !== input.value) input.value = digits;
        if (input.getAttribute('aria-invalid') && ZIP_RE.test(digits)) setError(false);
      });

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const zip = input.value.trim();
        if (!ZIP_RE.test(zip)) {
          setError(true);
          input.focus();
          return;
        }
        setError(false);
        writeFit({ zip });
        const ns = window.ShowerSimply && window.ShowerSimply.freight;
        const rateFn = (ns && typeof ns.getRate === 'function') ? ns.getRate : getFreightRate;
        const rate = await rateFn({
          endpoint: root.dataset.ssRateEndpoint || '',
          zip,
          productId: root.dataset.productId || '',
          variantId: currentVariantId(root),
          quantity: currentQuantity(root),
          weightLb: Number(root.dataset.shipWeightLb) || null,
        });
        showDeliveryResult(root, zip, rate);
      });
    });
  }

  /* ---------- Plan drawing: follow the selected variant ---------- */
  function onProductSelect(event) {
    if (!event || !event.promise || typeof event.promise.then !== 'function') return;
    const plans = document.querySelectorAll('[data-ss-plan]');
    if (!plans.length) return;
    event.promise
      .then((result) => {
        const html = result && result.detail && result.detail.html;
        if (!html || typeof html.querySelector !== 'function') return;
        plans.forEach((plan) => {
          const next = html.querySelector(`[data-ss-plan="${CSS.escape(plan.dataset.ssPlan)}"]`);
          if (next && next.innerHTML !== plan.innerHTML) plan.innerHTML = next.innerHTML;
        });
      })
      .catch(() => { /* aborted or failed variant fetch: keep the current drawing */ });
  }

  /* ---------- Spec downloads: file sizes ---------- */
  const formatBytes = (n) => {
    if (!Number.isFinite(n) || n <= 0) return '';
    if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
    return `${(n / (1024 * 1024)).toFixed(1).replace(/\.0$/, '')} MB`;
  };

  function initFileSizes(scope = document) {
    scope.querySelectorAll('[data-ss-file]').forEach((link) => {
      const meta = link.querySelector('[data-ss-file-meta]');
      if (!meta || meta.dataset.ssReady) return;
      meta.dataset.ssReady = '1';
      fetch(link.dataset.ssFile, { method: 'HEAD' })
        .then((res) => {
          const size = formatBytes(Number(res.headers.get('content-length')));
          if (res.ok && size) meta.textContent = `${meta.dataset.type}, ${size}`;
        })
        .catch(() => { /* size stays unknown; the type still shows */ });
    });
  }

  /* ---------- Fit results (collection pages) ----------
     Same fit rule as applyFitToCollection in ss-brand.js, which runs first on DOMContentLoaded and
     writes the note. This adds the sort, the optional hiding and the size controls. */
  const fitsCard = (card, w, d) =>
    w >= Number(card.dataset.fitWmin) && w <= Number(card.dataset.fitWmax) &&
    d >= Number(card.dataset.fitDmin) && d <= Number(card.dataset.fitDmax);

  const noteText = (fits, w, d) =>
    fits
      ? `${fits} ${fits === 1 ? 'shower fits' : 'showers fit'} a ${w} × ${d} in alcove.`
      : `Nothing in this collection fits a ${w} × ${d} in alcove exactly. Call us and we will find a size that works.`;

  function applyFit(section, w, d, { announce = false } = {}) {
    const list = section.querySelector('[data-ss-fit-list]');
    const cards = list ? Array.from(list.children) : [];
    let fits = 0;
    cards.forEach((card) => {
      const ok = 'fitWmin' in card.dataset && fitsCard(card, w, d);
      card.toggleAttribute('data-fits', ok);
      if (ok) fits += 1;
    });
    const order = (c) => Number(c.dataset.ssFitIndex) || 0;
    cards
      .slice()
      .sort((a, b) => Number(b.hasAttribute('data-fits')) - Number(a.hasAttribute('data-fits')) || order(a) - order(b))
      .forEach((card) => list.appendChild(card));
    const onlyFits = section.dataset.onlyFits === 'true' && fits > 0;
    cards.forEach((card) => { card.hidden = onlyFits && !card.hasAttribute('data-fits'); });

    const note = section.querySelector('[data-ss-fit-note]');
    if (note) {
      note.hidden = false;
      if (announce || !note.textContent.trim()) note.textContent = noteText(fits, w, d);
    }
  }

  function syncClearLink(section) {
    const clear = section.querySelector('[data-ss-fit-clear]');
    if (!clear) return;
    const url = new URL(location.href);
    ['w', 'd', 'zip'].forEach((k) => url.searchParams.delete(k));
    clear.href = url.pathname + url.search + url.hash;
  }

  function initFitResults(scope = document) {
    const params = new URLSearchParams(location.search);
    const w = Number(params.get('w'));
    const d = Number(params.get('d'));
    scope.querySelectorAll('[data-ss-fit-results]').forEach((section) => {
      const form = section.querySelector('[data-ss-fit-refine]');
      if (form && !form.dataset.ssReady) {
        form.dataset.ssReady = '1';
        const wi = form.querySelector('[name="w"]');
        const di = form.querySelector('[name="d"]');
        const saved = readFit();
        if (wi) wi.value = w || saved.w || wi.value;
        if (di) di.value = d || saved.d || di.value;
        form.addEventListener('submit', (event) => {
          if (!wi || !di || !form.checkValidity()) return; // let the browser explain the limits
          event.preventDefault();
          const nw = Number(wi.value);
          const nd = Number(di.value);
          const url = new URL(location.href);
          url.searchParams.set('w', String(nw));
          url.searchParams.set('d', String(nd));
          history.replaceState(history.state, '', url);
          writeFit({ w: nw, d: nd });
          section.hidden = false;
          applyFit(section, nw, nd, { announce: true });
          syncClearLink(section);
        });
      }
      syncClearLink(section);
      if (!w || !d) return;
      section.hidden = false;
      applyFit(section, w, d);
    });
  }

  /* ---------- Trade application ---------- */
  function initTradeForms(scope = document) {
    scope.querySelectorAll('[data-ss-business-type]').forEach((type) => {
      if (type.dataset.ssReady) return;
      type.dataset.ssReady = '1';
      const form = type.closest('form');
      const license = form && form.querySelector('[data-ss-license]');
      if (!license) return;
      const sync = () => {
        const option = type.options[type.selectedIndex];
        const needs = Boolean(option && option.hasAttribute('data-needs-license'));
        license.required = needs;
        license.toggleAttribute('aria-required', needs);
      };
      type.addEventListener('change', sync);
      sync();
    });
    const status = scope.querySelector('[data-ss-form-status]');
    if (status && typeof status.focus === 'function') status.focus();
  }

  /* ---------- Boot ---------- */
  function init(scope = document) {
    initDelivery(scope);
    initFileSizes(scope);
    initFitResults(scope);
    initTradeForms(scope);
  }

  // Run after ss-brand.js's DOMContentLoaded handlers so its fit note is already written.
  const boot = () => setTimeout(() => init(document), 0);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:product:select', onProductSelect);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => init(event.target.closest('.shopify-section') || document));

  window.ShowerSimply = Object.assign(window.ShowerSimply || {}, {
    // ss-freight.js (loaded first) provides the model-based estimate; keep it when present.
    freight: (window.ShowerSimply && window.ShowerSimply.freight) || { getRate: getFreightRate },
    fit: { read: readFit, write: writeFit },
  });
})();
